import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../components/config.js';
import './PublicProjectsList.css';

const PublicProjectsList = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/projects/public`);
      console.log('Fetched projects:', response.data);
      setProjects(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching public projects:', err);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'status-active',
      completed: 'status-completed'
    };
    
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>;
  };

  // Get projects with valid map locations
  const projectsWithLocations = projects.filter(project => {
    if (!project.mapData) return false;
    try {
      const mapData = typeof project.mapData === 'string' ? JSON.parse(project.mapData) : project.mapData;
      
      // Check for GeoJSON Point format
      if (mapData.type === 'Point' && mapData.coordinates && mapData.coordinates.length === 2) {
        console.log('Project with GeoJSON Point:', project.name, mapData.coordinates);
        return true;
      }
      
      // Check for simple coordinates array [lat, lng]
      if (mapData.coordinates && Array.isArray(mapData.coordinates) && mapData.coordinates.length === 2) {
        console.log('Project with coordinates:', project.name, mapData.coordinates);
        return true;
      }
      
      // Check for other GeoJSON types
      if (mapData.type && ['Polygon', 'LineString', 'MultiPolygon', 'MultiPoint', 'MultiLineString'].includes(mapData.type)) {
        console.log('Project with GeoJSON:', project.name, mapData.type);
        return true;
      }
      
      return false;
    } catch (e) {
      console.error('Error parsing mapData for project:', project.name, e);
      return false;
    }
  });

  console.log('Total projects:', projects.length);
  console.log('Projects with locations:', projectsWithLocations.length);

  if (loading) {
    return (
      <div className="public-projects-list">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-projects-list">
      <div className="page-header">
        <h1>Community Consultation Projects</h1>
        <p className="page-description">
          Red Sky Métis Independent Nation values community input on important projects. 
          Browse our current and completed projects, and share your thoughts to help us make informed decisions.
        </p>
        
        <div className="view-toggle">
          <button 
            className="toggle-btn active"
          >
            Grid View
          </button>
          <button 
            className="toggle-btn"
            onClick={() => navigate('/consultation/map')}
          >
            Map View
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {projects.length === 0 ? (
        <div className="no-projects">
          <div className="no-projects-icon">📋</div>
          <h2>No Public Projects Available</h2>
          <p>There are currently no projects open for public consultation. Please check back later.</p>
        </div>
      ) : (
        <div className="projects-container">
          <div className="projects-header">
            <h2>Current Projects ({projects.length})</h2>
            <p>Click on any project to learn more and share your feedback</p>
          </div>

          <div className="projects-grid">
            {projects.map(project => (
              <div key={project._id} className="project-card">
                <Link to={`/consultation/project/${project._id}`} className="project-link">
                  {project.imageUrl && (
                    <div className="project-image">
                      <img src={`${API_URL}${project.imageUrl}`} alt={project.name} />
                      <div className="image-overlay">
                        <span className="view-details">View Details & Comment</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="project-content">
                    <div className="project-header">
                      <h3>{project.name}</h3>
                      {getStatusBadge(project.status)}
                    </div>
                    
                    <p className="project-description">
                      {project.description.length > 120 
                        ? `${project.description.substring(0, 120)}...`
                        : project.description
                      }
                    </p>
                    
                    <div className="project-meta">
                      <div className="meta-item">
                        <span className="meta-icon">📅</span>
                        <span>Published: {formatDate(project.createdAt)}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-icon">💬</span>
                        <span>{project.commentCount} {project.commentCount === 1 ? 'comment' : 'comments'}</span>
                      </div>
                    </div>
                    
                    <div className="project-actions">
                      <span className="action-button">
                        Share Your Feedback →
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="consultation-info">
        <h2>About Community Consultation</h2>
        <div className="info-grid">
          <div className="info-card">
            <div className="info-icon">👥</div>
            <h3>Community-Driven</h3>
            <p>Your input shapes our decisions and helps us serve the community better.</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🔒</div>
            <h3>Anonymous Feedback</h3>
            <p>Share your thoughts anonymously - no personal information is required or stored.</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🌱</div>
            <h3>Meaningful Impact</h3>
            <p>Every comment is reviewed by our team and contributes to better project outcomes.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProjectsList;